import { useAuthStore, type UserRole } from "@/store/authStore";
import api from "@/lib/api";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Button } from "@gradebook/ui";

/** The one office being impersonated. See `ImpersonationState.effectiveRoles`. */
function impersonatedAs(roles: UserRole[]): UserRole | undefined {
  return roles[0];
}

const roleLabels: Record<UserRole, string> = {
  admin: "Admin",
  teacher: "Teacher",
  student: "Student",
  parent: "Parent",
  coordinator: "Coordinator",
};

const roleColors: Record<UserRole, string> = {
  admin: "bg-primary-600",
  teacher: "bg-info-600",
  student: "bg-success-600",
  parent: "bg-warning-600",
  coordinator: "bg-secondary-600",
};

export function ImpersonationBanner() {
  const { impersonation, stopImpersonation } = useAuthStore();

  if (!impersonation?.isImpersonating) {
    return null;
  }

  // Resolved once, and left undefined rather than defaulted to `student`. This
  // banner exists so nobody is confused about whose eyes the session is looking
  // through, and a confident "(Student)" over a role set that said nothing is
  // the one thing it must never print.
  const actingAs = impersonatedAs(impersonation.effectiveRoles);

  const handleStopImpersonating = async () => {
    try {
      const response = await api.post("/api/auth/stop-impersonating");
      stopImpersonation(response.data.token);
    } catch (error) {
      console.error("Failed to stop impersonating:", error);
    }
  };

  return (
    <div
      className={`${actingAs ? roleColors[actingAs] : 'bg-neutral-600'} text-white px-4 py-2 text-sm`}
    >
      <div className="mx-auto max-w-7xl flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-medium">Viewing as:</span>
          <span className="bg-white/20 px-2 py-0.5 rounded">
            {impersonation.impersonatedUser
              ? `${impersonation.impersonatedUser.firstName} ${impersonation.impersonatedUser.lastName}`
              : (actingAs ? roleLabels[actingAs] : 'someone else')}
          </span>
          <span className="text-white/80">
            ({actingAs ? roleLabels[actingAs] : 'unknown role'})
          </span>
        </div>
        <Button
          size="sm"
          onClick={handleStopImpersonating}
          className="gap-1 rounded bg-white/20 text-white hover:bg-white/30"
        >
          <XMarkIcon className="size-4" />
          <span>Exit</span>
        </Button>
      </div>
    </div>
  );
}
